import { ChangeDetectionStrategy, Component, Input, input, OnDestroy, output, Provider } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { IDisposable, editor as monacoEditor } from 'monaco-editor';
import { EditorComponent, NGX_MONACO_EDITOR_CONFIG, NgxMonacoEditorConfig } from 'ngx-monaco-editor-v2';

export function providePythonEditor(): Provider {
  const monacoConfig: NgxMonacoEditorConfig = {
    baseUrl: 'assets',
  };

  return {
    provide: NGX_MONACO_EDITOR_CONFIG,
    useValue: monacoConfig,
  };
}

export interface KeyboardBinding {
  id: string;
  keybindings: number[];
}

@Component({
  selector: 'dojo-python-editor',
  template: `
    <ngx-monaco-editor
      #editor
      style="height: 100%; width: 100%"
      [options]="editorOptions"
      [formControl]="formCtrl"
      (onInit)="onInit($event)"
    />
  `,
  imports: [EditorComponent, ReactiveFormsModule],
  providers: [providePythonEditor()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PythonEditorComponent implements OnDestroy {
  @Input() formCtrl!: FormControl<string>;

  readonly keyboardBindings = input<KeyboardBinding[]>([]);

  readonly keyboardEvents = output<string>();

  readonly editorInitialized = output<monacoEditor.IStandaloneCodeEditor>();

  editor: monacoEditor.IStandaloneCodeEditor | undefined = undefined;

  private readonly disposables: IDisposable[] = [];

  readonly editorOptions: monacoEditor.IStandaloneEditorConstructionOptions = {
    automaticLayout: true,
    theme: 'vs',
    language: 'python',
    scrollBeyondLastLine: false,
    minimap: { enabled: true },
    lineHeight: 20,
    fontSize: 14,
    wordWrap: 'on',
    wrappingIndent: 'indent',
  };

  onInit(editor: monacoEditor.IStandaloneCodeEditor) {
    this.editor = editor;
    this.editorInitialized.emit(editor);

    this.keyboardBindings().forEach(({ id, keybindings }) => {
      this.disposables.push(
        editor.addAction({
          id,
          label: '',
          keybindings,
          run: () => {
            this.keyboardEvents.emit(id);
          },
        }),
      );
    });
  }

  ngOnDestroy() {
    this.disposables.forEach((disposable) => disposable.dispose());
  }
}
